import {
  useState, useRef, useEffect, useMemo, useCallback,
} from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { useReactToPrint } from 'react-to-print'
import { formatDatePlusThree } from 'src/utils/formatDate'
import { actions, initialState } from '../../RestockingState'
import {
  PageStatusFilterIds, ItemsListPayload, PageStatus, UnlinkOrigin,
} from '../../RestockingTypes'
import UnlinkPageWithBadge from '../UnlinkPageWithBadge/UnlinkPageWithBadge'
import UnlinkPageButton from '../UnlinkPageWithBadge/UnlinkPageButton'
import { FormManageFilterLists } from '../FormManageFilterLists/FormManageFilterLists'
import { useHistory, useParams } from 'react-router-dom'
import { DeleteOutline, OpenInNew } from '@mui/icons-material'
import { capitalizeString } from 'src/utils/capitalize'
import { isEqual } from 'lodash'
import ListRestockingToPrintRepeatedly from '../ListRestockingToPrint/ListRestockingToPrint'
import TableMui from 'src/components/TableMui'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import * as S from './TableListPageManagement.styles'

interface Params {
  idList: string
}

export const TableListPageManagement = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [statusRestockingLists, setStatusRestockingLists] = useState<number>(PageStatusFilterIds.ALL_OPTIONS)
  const [openModalRemovePage, setOpenModalRemovePage] = useState(false)
  const [idOfPageToRemove, setIdOfPageToRemove] = useState<number>()
  const [idOfPageToUnlink, setIdOfPageToUnlink] = useState<number>()
  const [selectedItemsToPrint, setSelectedItemsToPrint] = useState<number[]>([])
  const [shouldPrint, setShouldPrint] = useState(false)
  const { restockingItemsList, restockingListPages, selectedFiltersHistory } = useSelector((state) => state.restocking)
  const { idList } = useParams<Params>()

  const refetchPages = useCallback(() => {
    if (idList) {
      dispatch(actions.getRestockingListPages(Number(idList)))
    }
  }, [dispatch, idList])

  useEffect(() => {
    refetchPages()
  }, [refetchPages])

  const isPageClosed = (status: string) => status === PageStatus.FINISHED || status === PageStatus.CANCELED

  const onClickRemovePage = (idPage: number) => {
    setIdOfPageToRemove(idPage)
    setOpenModalRemovePage(true)
  }

  const pagesFilteredByStatus = restockingListPages?.filter((item) => {
    switch (statusRestockingLists) {
      case PageStatusFilterIds.ALL_OPTIONS:
        return true

      case PageStatusFilterIds.OPENED:
        return item.status === PageStatus.CREATED
          || item.status === PageStatus.STARTED
          || item.status === PageStatus.PAUSED

      case PageStatusFilterIds.CLOSED:
        return item.status === PageStatus.FINISHED || item.status === PageStatus.CANCELED

      default:
        return true
    }
  })

  const resolveTableRows = useMemo(() =>
    pagesFilteredByStatus.map(({
      id, createdAt, startedAt, finishedAt, status, totalSkus, keeper, mobileAddress,
    }) => ({
      id,
      createdAt: formatDatePlusThree(createdAt),
      startedAt: formatDatePlusThree(startedAt),
      finishedAt: formatDatePlusThree(finishedAt),
      status: capitalizeString(status),
      totalSkus,
      keeper,
      mobileAddress: mobileAddress || '-',
      unlink: (
        <UnlinkPageButton
          disabled={isPageClosed(status) || !keeper}
          disabledTooltip={keeper ? 'Página encerrada' : 'Não há keeper para desvincular'}
          onClick={() => setIdOfPageToUnlink(id)}
        />
      ),
      open: (
        <OpenInNew
          sx={{ cursor: 'pointer' }}
          onClick={() => history.push(`/restocking/manage/page/${id}/details`)}
          data-testid='open-page-button-test'
        />
      ),
      remove: (
        <DeleteOutline
          sx={{ color: 'red', cursor: 'pointer' }}
          onClick={(event) => {
            event.stopPropagation()
            onClickRemovePage(id)
          }}
          data-testid='trash-button-test'
        />
      ),
    })), [history, pagesFilteredByStatus])

  const componentRef = useRef(null)

  const print = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => setSelectedItemsToPrint([]),
  })

  const filesToPrint = restockingItemsList?.map((item) => (
    <S.PrintPage>
      <ListRestockingToPrintRepeatedly key={item.id} itemsList={item} />
    </S.PrintPage>
  ))

  const handlePrintSelected = () => {
    const payload: ItemsListPayload = {
      ids: selectedItemsToPrint,
    }

    dispatch(actions.getItemsList(payload))
    setShouldPrint(true)
  }

  useEffect(() => {
    if (restockingItemsList?.length > 0 && shouldPrint) {
      print()
      setShouldPrint(false)
    }
  }, [restockingItemsList, print, shouldPrint])

  const closeModal = () => setOpenModalRemovePage(false)

  const removeRestockingListPage = (idPage: number) => {
    dispatch(actions.removeRestockingItem({
      commonId: Number(idList),
      idToDelete: idPage,
      endpoint: `/page/${idPage}`,
      itemName: 'página',
      stateName: 'restockingListPages',
      history,
    }))
    closeModal()
  }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = resolveTableRows?.map((row) => row.id) || []
      setSelectedItemsToPrint(newSelected)
      return
    }
    setSelectedItemsToPrint([])
  }

  const handleClick = (id: number) => {
    const selectedIndex = selectedItemsToPrint.findIndex((itemId) => itemId === id)

    if (selectedIndex === -1) {
      setSelectedItemsToPrint([...selectedItemsToPrint, id])
    } else {
      const updatedSelectedItems = selectedItemsToPrint.filter((itemId) => itemId !== id)
      setSelectedItemsToPrint(updatedSelectedItems)
    }
  }

  const goBack = () => {
    const hasHistorySelectedFilters = !isEqual(selectedFiltersHistory, initialState.selectedFiltersHistory)

    if (hasHistorySelectedFilters) {
      dispatch(actions.setAutoFetchRestockingLists(true))
    }

    history.push('/restocking/manage')
  }

  return (
    <S.Wrapper>
      <FormManageFilterLists
        statusRestockingLists={statusRestockingLists}
        setStatusRestockingLists={setStatusRestockingLists}
      />
      <TableMui
        columns={[
          { id: 'createdAt', label: 'Data de criação' },
          { id: 'totalSkus', label: 'Qtd. de SKUs' },
          { id: 'status', label: 'Status' },
          { id: 'startedAt', label: 'Data/hora do início' },
          { id: 'finishedAt', label: 'Data/hora do fim' },
          { id: 'keeper', label: 'Keeper Responsável' },
          { id: 'mobileAddress', label: 'Endereço móvel' },
          { id: 'unlink', label: 'Desvincular' },
          { id: 'open', label: 'Abrir' },
          { id: 'remove', label: 'Remover' },
        ]}
        emptyLabel='Nenhuma página para exibir'
        rows={resolveTableRows}
        rowCount={resolveTableRows.length}
        selectedCount={selectedItemsToPrint.length}
        selected={selectedItemsToPrint.map((item) => item)}
        onSelectAllClick={handleSelectAllClick}
        onRowClick={handleClick}
        enablePagination
        rowsPerPageLimit={25}
        maxHeight='62vh'
        testId='table-list-pages-test'
        hasCheckbox
      />
      <S.ButtonContainer>
        <S.CustomButton
          title='Voltar'
          type='button'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          onClick={goBack}
          data-testid='back-button'
        />
        <S.CustomButtonPrint
          variant='contained'
          size='large'
          type='button'
          disabled={selectedItemsToPrint.length === 0}
          onClick={handlePrintSelected}
          data-testId='print-selected-button'
        >
          Imprimir páginas selecionadas
        </S.CustomButtonPrint>
      </S.ButtonContainer>
      <S.PrintArea ref={componentRef}>
        {filesToPrint}
      </S.PrintArea>
      <UnlinkPageWithBadge
        idPage={idOfPageToUnlink}
        origin={UnlinkOrigin.PAGES_LIST}
        open={idOfPageToUnlink !== undefined}
        onOpenChange={(open) => !open && setIdOfPageToUnlink(undefined)}
        onSuccess={refetchPages}
      />
      {
        idOfPageToRemove
        && (
          <ConfirmOrRejectModal
            show={openModalRemovePage}
            title='Remover página'
            description='Você tem certeza que deseja remover a página?'
            onConfirm={() => removeRestockingListPage(idOfPageToRemove)}
            onReject={closeModal}
            onClose={closeModal}
            dataTestId='modal-confirmation-delete-page'
          />
        )
      }
    </S.Wrapper>
  )
}
